import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";
import {
  buildSectionErrorMessage,
  createEmptyBoardSummary,
  createEmptyBoardTrends,
  createEmptyBpiMonthlyActivityOverview,
  createEmptyBpiOverview,
  createEmptyEventInsights,
  createEmptyPipeline,
  createEmptyProcessOverview,
  EMPTY_STATS_ERRORS,
  getStatsDateRange,
  normalizeBoardCenters,
  normalizeBoardSummary,
  normalizeBoardTrends,
  normalizeBpiMonthlyActivityOverview,
  normalizeBpiOverview,
  normalizeEventInsights,
  normalizeProcessOverview,
  normalizeStatsFilters,
  normalizeStatsFunnel,
  normalizeStatsPipeline,
} from "./statsDashboard";
import type {
  StatsAvailableFilters,
  StatsBpiMonthlyActivityOverview,
  StatsBpiOverview,
  StatsBoardCenterRow,
  StatsBoardSummary,
  StatsBoardTrends,
  StatsDashboardErrors,
  StatsEventInsights,
  StatsFunnelStep,
  StatsPipeline,
  StatsProcessOverview,
  StatsRangeKey,
} from "./statsDashboardTypes";

interface UseStatsDashboardParams {
  brand: string;
  rangeKey: StatsRangeKey;
  selectedCenters: string[];
  selectedClientTypes: string[];
}

interface UseStatsDashboardResult {
  loading: boolean;
  filters: StatsAvailableFilters;
  boardSummary: StatsBoardSummary;
  boardTrends: StatsBoardTrends;
  boardCenters: StatsBoardCenterRow[];
  bpiOverview: StatsBpiOverview;
  bpiMonthlyActivity: StatsBpiMonthlyActivityOverview;
  eventInsights: StatsEventInsights;
  processOverview: StatsProcessOverview;
  funnel: StatsFunnelStep[];
  pipeline: StatsPipeline;
  errors: StatsDashboardErrors;
}

export function useStatsDashboard({
  brand,
  rangeKey,
  selectedCenters,
  selectedClientTypes,
}: UseStatsDashboardParams): UseStatsDashboardResult {
  const [loading, setLoading] = useState(true);
  const [documentRevision, setDocumentRevision] = useState(0);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const refresh = () => { clearTimeout(timer); timer = setTimeout(() => setDocumentRevision((revision) => revision + 1), 250); };
    window.addEventListener("documents-changed", refresh);
    const channel = supabase.channel("stats-document-archives")
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "quotes" }, refresh)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "invoices" }, refresh)
      .subscribe();
    return () => { clearTimeout(timer); window.removeEventListener("documents-changed", refresh); supabase.removeChannel(channel); };
  }, []);

  const [filters, setFilters] = useState<StatsAvailableFilters>({
    centers: [],
    clientTypes: [],
  });
  const [boardSummary, setBoardSummary] = useState<StatsBoardSummary>(createEmptyBoardSummary());
  const [boardTrends, setBoardTrends] = useState<StatsBoardTrends>(createEmptyBoardTrends());
  const [boardCenters, setBoardCenters] = useState<StatsBoardCenterRow[]>([]);
  const [bpiOverview, setBpiOverview] = useState<StatsBpiOverview>(createEmptyBpiOverview());
  const [bpiMonthlyActivity, setBpiMonthlyActivity] = useState<StatsBpiMonthlyActivityOverview>(
    createEmptyBpiMonthlyActivityOverview(),
  );
  const [eventInsights, setEventInsights] = useState<StatsEventInsights>(
    createEmptyEventInsights(),
  );
  const [processOverview, setProcessOverview] = useState<StatsProcessOverview>(
    createEmptyProcessOverview(),
  );
  const [funnel, setFunnel] = useState<StatsFunnelStep[]>([]);
  const [pipeline, setPipeline] = useState<StatsPipeline>(createEmptyPipeline());
  const [errors, setErrors] = useState<StatsDashboardErrors>(EMPTY_STATS_ERRORS);

  const { fromIso, toIso } = useMemo(() => {
    const { from, to } = getStatsDateRange(rangeKey);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
    };
  }, [rangeKey]);

  const normalizedCenters = useMemo(
    () =>
      [...new Set(selectedCenters.map((value) => value.trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "fr"),
      ),
    [selectedCenters],
  );

  const normalizedClientTypes = useMemo(
    () =>
      [...new Set(selectedClientTypes.map((value) => value.trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "fr"),
      ),
    [selectedClientTypes],
  );

  const selectedCentersKey = normalizedCenters.join("\u0001");
  const selectedClientTypesKey = normalizedClientTypes.join("\u0001");

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      setLoading(true);

      const params = {
        p_brand: brand,
        p_from: fromIso,
        p_to: toIso,
        p_centers: normalizedCenters.length > 0 ? normalizedCenters : null,
        p_client_types: normalizedClientTypes.length > 0 ? normalizedClientTypes : null,
      };

      const bpiParams = {
        p_brand: brand,
        p_from: fromIso,
        p_to: toIso,
        p_centers: normalizedCenters.length > 0 ? normalizedCenters : null,
      };

      const results = await Promise.allSettled([
        supabase.rpc("stats_dashboard_filters", params),
        supabase.rpc("stats_dashboard_board_summary", params),
        supabase.rpc("stats_dashboard_board_trends", params),
        supabase.rpc("stats_dashboard_board_centers", params),
        supabase.rpc("stats_dashboard_bpi_monthly_overview", bpiParams),
        supabase.rpc("stats_dashboard_bpi_monthly_activity_overview", bpiParams),
        supabase.rpc("stats_dashboard_event_insights", params),
        supabase.rpc("stats_dashboard_process", params),
        supabase.rpc("stats_dashboard_funnel", params),
        supabase.rpc("stats_dashboard_pipeline", params),
      ]);

      if (cancelled) return;

      const nextErrors: StatsDashboardErrors = { ...EMPTY_STATS_ERRORS };

      const [
        filtersResult,
        boardSummaryResult,
        boardTrendsResult,
        boardCentersResult,
        bpiOverviewResult,
        bpiMonthlyActivityResult,
        eventInsightsResult,
        processResult,
        funnelResult,
        pipelineResult,
      ] = results;

      if (filtersResult.status === "fulfilled" && !filtersResult.value.error) {
        setFilters(normalizeStatsFilters(filtersResult.value.data));
      } else {
        nextErrors.filters = buildSectionErrorMessage(
          "Filtres",
          filtersResult.status === "fulfilled" ? filtersResult.value.error : filtersResult.reason,
        );
        setFilters({ centers: [], clientTypes: [] });
      }

      if (boardSummaryResult.status === "fulfilled" && !boardSummaryResult.value.error) {
        setBoardSummary(normalizeBoardSummary(boardSummaryResult.value.data));
      } else {
        nextErrors.boardSummary = buildSectionErrorMessage(
          "Résumé board",
          boardSummaryResult.status === "fulfilled"
            ? boardSummaryResult.value.error
            : boardSummaryResult.reason,
        );
        setBoardSummary(createEmptyBoardSummary());
      }

      if (boardTrendsResult.status === "fulfilled" && !boardTrendsResult.value.error) {
        setBoardTrends(normalizeBoardTrends(boardTrendsResult.value.data));
      } else {
        nextErrors.boardTrends = buildSectionErrorMessage(
          "Tendances",
          boardTrendsResult.status === "fulfilled"
            ? boardTrendsResult.value.error
            : boardTrendsResult.reason,
        );
        setBoardTrends(createEmptyBoardTrends());
      }

      if (boardCentersResult.status === "fulfilled" && !boardCentersResult.value.error) {
        setBoardCenters(normalizeBoardCenters(boardCentersResult.value.data));
      } else {
        nextErrors.boardCenters = buildSectionErrorMessage(
          "Performance par centre",
          boardCentersResult.status === "fulfilled"
            ? boardCentersResult.value.error
            : boardCentersResult.reason,
        );
        setBoardCenters([]);
      }

      if (bpiOverviewResult.status === "fulfilled" && !bpiOverviewResult.value.error) {
        setBpiOverview(normalizeBpiOverview(bpiOverviewResult.value.data));
      } else {
        nextErrors.bpiOverview = buildSectionErrorMessage(
          "Pilotage BPI",
          bpiOverviewResult.status === "fulfilled"
            ? bpiOverviewResult.value.error
            : bpiOverviewResult.reason,
        );
        setBpiOverview(createEmptyBpiOverview());
      }

      if (
        bpiMonthlyActivityResult.status === "fulfilled" &&
        !bpiMonthlyActivityResult.value.error
      ) {
        setBpiMonthlyActivity(
          normalizeBpiMonthlyActivityOverview(bpiMonthlyActivityResult.value.data),
        );
      } else {
        nextErrors.bpiMonthlyActivity = buildSectionErrorMessage(
          "Volumes mensuels BPI",
          bpiMonthlyActivityResult.status === "fulfilled"
            ? bpiMonthlyActivityResult.value.error
            : bpiMonthlyActivityResult.reason,
        );
        setBpiMonthlyActivity(createEmptyBpiMonthlyActivityOverview());
      }

      if (eventInsightsResult.status === "fulfilled" && !eventInsightsResult.value.error) {
        setEventInsights(normalizeEventInsights(eventInsightsResult.value.data));
      } else {
        nextErrors.eventInsights = buildSectionErrorMessage(
          "Événements confirmés",
          eventInsightsResult.status === "fulfilled"
            ? eventInsightsResult.value.error
            : eventInsightsResult.reason,
        );
        setEventInsights(createEmptyEventInsights());
      }

      if (processResult.status === "fulfilled" && !processResult.value.error) {
        setProcessOverview(normalizeProcessOverview(processResult.value.data));
      } else {
        nextErrors.process = buildSectionErrorMessage(
          "Suivi process",
          processResult.status === "fulfilled" ? processResult.value.error : processResult.reason,
        );
        setProcessOverview(createEmptyProcessOverview());
      }

      if (funnelResult.status === "fulfilled" && !funnelResult.value.error) {
        setFunnel(normalizeStatsFunnel(funnelResult.value.data));
      } else {
        nextErrors.funnel = buildSectionErrorMessage(
          "Parcours commercial",
          funnelResult.status === "fulfilled" ? funnelResult.value.error : funnelResult.reason,
        );
        setFunnel([]);
      }

      if (pipelineResult.status === "fulfilled" && !pipelineResult.value.error) {
        setPipeline(normalizeStatsPipeline(pipelineResult.value.data));
      } else {
        nextErrors.pipeline = buildSectionErrorMessage(
          "Portefeuille actuel",
          pipelineResult.status === "fulfilled"
            ? pipelineResult.value.error
            : pipelineResult.reason,
        );
        setPipeline(createEmptyPipeline());
      }

      setErrors(nextErrors);
      setLoading(false);
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, [brand, fromIso, selectedCentersKey, selectedClientTypesKey, toIso, documentRevision]);

  return {
    loading,
    filters,
    boardSummary,
    boardTrends,
    boardCenters,
    bpiOverview,
    bpiMonthlyActivity,
    eventInsights,
    processOverview,
    funnel,
    pipeline,
    errors,
  };
}
